import { useRef, useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogTitle,
} from '@/components/ui/dialog';
import { track } from '@/lib/analytics';
import { HOW_IT_WORKS_EVENT, type HowItWorksSource } from '../welcome-steps';
import { PhoneVideo } from './PhoneVideo';

interface HowItWorksDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** De onde foi aberto — vai no evento. */
  source: HowItWorksSource;
}

/**
 * O vídeo de apresentação fora do wizard, para rever a qualquer hora.
 *
 * Sem bloqueio nenhum: aqui a pessoa já aceitou os termos e o vídeo é consulta,
 * então fecha por X, Esc e clique fora como qualquer diálogo.
 *
 * O evento de abertura sai daqui (e não do botão) para que qualquer gatilho —
 * cabeçalho, menu do celular — conte do mesmo jeito. O de fechamento leva até
 * onde a pessoa viu: é o que diz se o vídeo está longo demais para uma
 * consulta.
 */
export function HowItWorksDialog({
  open,
  onOpenChange,
  source,
}: HowItWorksDialogProps) {
  const progress = useRef(0);
  const [completed, setCompleted] = useState(false);

  const change = (next: boolean) => {
    if (next) {
      progress.current = 0;
      setCompleted(false);
      track(HOW_IT_WORKS_EVENT.opened, { source });
    } else {
      track(HOW_IT_WORKS_EVENT.closed, {
        source,
        completed,
        ...(completed ? {} : { video_progress_pct: progress.current }),
      });
    }
    onOpenChange(next);
  };

  return (
    <Dialog open={open} onOpenChange={change}>
      <DialogContent className="max-w-md gap-4 p-6 sm:p-8">
        <DialogTitle className="pr-8 text-2xl leading-tight">
          Veja como funciona
        </DialogTitle>
        <DialogDescription className="sr-only">
          Vídeo de apresentação do Mercado Único: como comprar, pagamento e
          prazo de entrega.
        </DialogDescription>
        {/* Monta só aberto: o `<video>` não fica carregado atrás da vitrine e
            cada abertura começa do zero. */}
        {open && (
          <PhoneVideo
            onStarted={(how) =>
              track(HOW_IT_WORKS_EVENT.videoStarted, { source, ...how })
            }
            onPaused={(progressPct) =>
              track(HOW_IT_WORKS_EVENT.videoPaused, {
                source,
                progress_pct: progressPct,
              })
            }
            onProgress={(progressPct) => {
              progress.current = progressPct;
            }}
            onEnded={() => {
              setCompleted(true);
              track(HOW_IT_WORKS_EVENT.videoCompleted, { source });
            }}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
